import type { impostazioni as it } from "../it/impostazioni.ts";
import { formati } from "../formati.ts";

const f = formati("en");

/** La pagina Impostazioni: utenti, registry, token di lettura, email, cartelle, backup, lingua. */
export const impostazioni: typeof it = {
  "impostazioni.intro": "Who can access Portman, where it pulls images from, how it alerts you, and how you back it up.",
  "impostazioni.sezioni": "Section",
  "impostazioni.comeFunziona": "How it works",
  "impostazioni.nessunaSezione": "You don't have permission for any settings section.",

  "accessi.titolo": "Unauthorized access",
  "accessi.intro":
    "Failed sign-ins, grouped by address. A blocked address is refused on every request, " +
    "sign-in and webhooks included. The address is blocked, not the username, which the attacker picks.",
  "accessi.tentativi": "Failed attempts",
  "accessi.nessunTentativo": "No failed sign-ins recorded.",
  "accessi.colIp": "IP address",
  "accessi.colFalliti": "Failed",
  "accessi.colUtenti": "Usernames tried",
  "accessi.colUltimo": "Last attempt",
  "accessi.riusciti": (p: { n: number }) => `${p.n} successful`,
  "accessi.riuscitiAiuto": "Someone also signed in from this address: it may be a user who mistyped the password.",
  "accessi.bloccato": "blocked",
  "accessi.tuoIp": "your address",
  "accessi.blocca": "Block",
  "accessi.sblocca": "Unblock",
  "accessi.bloccati": "Blocked addresses",
  "accessi.nessunBloccato": "No blocked addresses.",
  "accessi.ipSegnaposto": "203.0.113.7",
  "accessi.motivo": "Reason (optional)",
  "accessi.bloccatoDa": (p: { utente: string; quando: string }) => `blocked by ${p.utente} on ${p.quando}`,

  "generale.titolo": "General",
  "generale.intro": "The instance language applies to emails and server logs.",
  "generale.lingua": "Instance language",
  "generale.daAmbiente": "Set by PORTMAN_LANG: to change it, edit the variable and restart Portman.",

  "registry.titolo": "Private registries",
  "registry.intro":
    "Credentials stay encrypted in the database. Portman hands them to Docker only for the duration of a pull, " +
    "in a temporary file it deletes right after.",
  "registry.aggiungi": "Add registry",
  "registry.vuoto":
    "No registries configured: Portman pulls public images and, for private ones, " +
    "relies on the machine's Docker configuration.",
  "registry.indirizzo": "Address",
  "registry.indirizzoAiuto": "The host as it appears in the image name. For Docker Hub: docker.io.",
  "registry.utente": "Username",
  "registry.password": "Password or token",
  "registry.passwordAiuto": "It won't be visible again after saving.",
  "registry.salva": "Save credentials",

  "token.durata1h": "1 hour",
  "token.durata1g": "1 day",
  "token.durata7g": "7 days",
  "token.durata30g": "30 days",
  "token.revocato": "revoked",
  "token.scaduto": "expired",
  "token.creatoreNonValido": "invalid creator",
  "token.senzaStack": "no stacks",
  "token.titolo": "Read tokens",
  "token.intro":
    "Give external tools read-only access to the list, status and logs of the chosen stacks. " +
    "They go in the Authorization header, never in the URL, and expire with their creator.",
  "token.crea": "Create token",
  "token.vuoto": "No read tokens.",
  "token.revocaConferma": (p: { nome: string }) =>
    `Revoke the token "${p.nome}"? Tools that use it will stop working.`,
  "token.nessunoStack": "no stacks",
  "token.scade": (p: { quando: string }) => `expires ${p.quando}`,
  "token.ultimoUso": (p: { quando: string; ip: string | null }) =>
    `last used ${p.quando}${p.ip ? ` from ${p.ip}` : ""}`,
  "token.creatoDa": (p: { chi: string }) => `created by ${p.chi}`,
  "token.creatoreRimosso": "creator removed",
  "token.nomeAiuto": "To recognize it in the list, for example the tool that uses it.",
  "token.durata": "Duration",
  "token.durataAiuto": "It stops working when it expires: it doesn't renew.",
  "token.stackLeggibili": "Readable stacks",
  "token.nessunoStackDisponibile": "No stacks available.",
  "token.copialoOra": "It won't be visible again: copy it now.",
  "token.copia": "Copy token",
  "token.copiaFallita": "Copy unavailable: select the token and copy it manually.",

  "guida.passo1": "Create it with only the stacks you need and the shortest duration that works.",
  "guida.passo2":
    "Copy it and save it in a file only you can read, outside any repository. On macOS, right after «Copy token»:",
  "guida.passo2Altrove":
    "Elsewhere, paste it into the file with an editor, then chmod 600. Don't put it in a command like echo: " +
    "it would stay in your shell history.",
  "guida.passo3": "Check which stacks it can read:",
  "guida.passo3Log": "and follow a stack's logs in real time:",
  "guida.passo4":
    "With an assistant like Claude Code, give it the file path and the stack id. " +
    "Don't paste the token into the chat: it would stay in the transcript even after you revoke it.",
  "guida.passo5": "When you're done, revoke it below.",

  "email.titoloSezione": "Email and alerts",
  "email.intro":
    "SMTP server, recipients and global thresholds: each stack can override the thresholds from its Resources tab. " +
    "The password is encrypted and never shown.",
  "email.server": "SMTP server",
  "email.porta": "Port",
  "email.sicurezza": "Security",
  "email.sicurezzaNessuna": "None",
  "email.passwordImpostata": "set — leave empty to keep it",
  "email.mittente": "Sender",
  "email.mittenteAiuto": "The address emails are sent from.",
  "email.indirizzoPubblico": "Portman public URL",
  "email.indirizzoPubblicoAiuto": "Base for links in emails.",
  "email.destinatari": "Recipients",
  "email.destinatariAiuto": "One address per line.",
  "email.cpuOltre": "CPU above (%)",
  "email.perMinuti": "for (minutes)",
  "email.memoriaOltre": "Memory above (%)",
  "email.provaInviata": "Test email sent.",
  "email.inviaProva": "Send test email",
  "email.salvaPrima": "Save before sending the test: it uses the saved settings.",
  "email.connessione": "Connecting to the SMTP server, up to 15 seconds…",

  "cartelle.titolo": "Folders",
  "cartelle.intro": "The host folders that stacks mount as bind mounts.",
  "cartelle.comeFunziona":
    "Bind mount sources for stacks, created by Portman (uid 1000) instead of the Docker daemon: " +
    "on a share with root_squash, the daemon's root can't chown them.",
  "cartelle.nonAttiva":
    "Not active. Set PORTMAN_STORAGE_ROOT (e.g. /srv/shared-storage) and mount that folder " +
    "into the Portman container at the same path.",
  "cartelle.filtro": "Filter by name…",
  "cartelle.contoFiltrato": (p: { n: number; totale: number }) =>
    `${f.numero(p.n)} of ${f.numero(p.totale)} ${f.plurale(p.totale, { one: "folder", other: "folders" })}`,
  "cartelle.conto": (p: { n: number }) => `${f.numero(p.n)} ${f.plurale(p.n, { one: "folder", other: "folders" })}`,
  "cartelle.nessuna": "No subfolders.",
  "cartelle.nessunaFiltro": "No folders match the filter.",
  "cartelle.nuovaSegnaposto": "new-folder  (also test01/test01_uploads)",
  "cartelle.crea": "Create folder",
  "cartelle.eliminaConferma": (p: { nome: string }) => `Delete the folder "${p.nome}"? It must be empty.`,
  "cartelle.elimino": "Deleting…",

  "database.titolo": "Backup",
  "database.intro": "A copy of the database with everything Portman knows: download it, or restore one.",
  "database.comeFunziona":
    "A copy contains everything: stacks, revisions, users and secrets — the latter encrypted with the current master key. " +
    "Keep the master key with the backup: without it, restored stacks no longer know their passwords. " +
    "Only the metrics and alert history are left out; it starts over after a restore.",
  "database.scarica": "Download a copy",
  "database.scaricaAiuto": "Taken without stopping Portman, consistent even during a deploy.",
  "database.scaricaBackup": "Download backup",
  "database.ripristina": "Restore a copy",
  "database.ripristinaAiuto":
    "The current database is set aside as portman-prima-del-ripristino.db in the data folder, " +
    "then Portman restarts. If it isn't supervised — in a container with a restart policy, or under systemd — restart it manually.",
  "database.ripristinaConferma": (p: { file: string }) =>
    `Restore "${p.file}"? Portman's current content — stacks, users and history — is replaced by the backup's.`,
};
